import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge, ListGroup, Card } from 'react-bootstrap';
import { mockData } from '../../data/fakerData';

interface BannerGroupData {
  id?: string;
  name: string;
  description: string;
  position: string;
  isActive: boolean;
  autoPlay: boolean;
  interval: number;
  showIndicators: boolean;
  showControls: boolean;
  bannerIds: string[];
}

interface BannerGroupModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (group: BannerGroupData) => void;
  group?: BannerGroupData | null;
  mode: 'add' | 'edit';
}

const BannerGroupModal: React.FC<BannerGroupModalProps> = ({ show, onHide, onSave, group, mode }) => {
  const [formData, setFormData] = useState<BannerGroupData>({
    name: '',
    description: '',
    position: 'Hero',
    isActive: true,
    autoPlay: true,
    interval: 5,
    showIndicators: true,
    showControls: true,
    bannerIds: []
  });

  const [selectedBanner, setSelectedBanner] = useState('');

  useEffect(() => {
    if (group && mode === 'edit') {
      setFormData(group);
    } else if (mode === 'add') {
      setFormData({
        name: '',
        description: '',
        position: 'Hero',
        isActive: true,
        autoPlay: true,
        interval: 5,
        showIndicators: true,
        showControls: true,
        bannerIds: []
      });
    }
  }, [group, mode, show]);

  const handleInputChange = (field: keyof BannerGroupData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddBanner = () => {
    if (selectedBanner && !formData.bannerIds.includes(selectedBanner)) {
      setFormData(prev => ({
        ...prev,
        bannerIds: [...prev.bannerIds, selectedBanner]
      }));
      setSelectedBanner('');
    }
  };

  const handleRemoveBanner = (bannerId: string) => {
    setFormData(prev => ({
      ...prev,
      bannerIds: prev.bannerIds.filter(id => id !== bannerId)
    }));
  };

  const moveBanner = (index: number, direction: 'up' | 'down') => {
    const newBannerIds = [...formData.bannerIds];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newBannerIds.length) {
      [newBannerIds[index], newBannerIds[newIndex]] = [newBannerIds[newIndex], newBannerIds[index]];
      setFormData(prev => ({ ...prev, bannerIds: newBannerIds }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.bannerIds.length === 0) {
      alert('Please add at least one banner to the carousel');
      return;
    }
    onSave(formData);
    onHide();
  };

  const getBannerById = (id: string) => {
    return mockData.banners.find(b => b.id === id);
  };

  const availableBanners = mockData.banners.filter(
    banner => !formData.bannerIds.includes(banner.id)
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'add' ? 'Create Banner Carousel' : 'Edit Banner Carousel'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Carousel Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  placeholder="e.g., Home Page Hero Carousel"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Position</Form.Label>
                <Form.Select
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                >
                  <option value="Hero">Hero</option>
                  <option value="Featured">Featured</option>
                  <option value="Promotional">Promotional</option>
                  <option value="Sidebar">Sidebar</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Brief description of this carousel"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="isActive"
                label="Active"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="autoPlay"
                label="Auto-play"
                checked={formData.autoPlay}
                onChange={(e) => handleInputChange('autoPlay', e.target.checked)}
                className="mb-2"
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="showIndicators"
                label="Show Indicators (dots)"
                checked={formData.showIndicators}
                onChange={(e) => handleInputChange('showIndicators', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="showControls"
                label="Show Controls (arrows)"
                checked={formData.showControls}
                onChange={(e) => handleInputChange('showControls', e.target.checked)}
                className="mb-2"
              />
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Slide Interval (seconds)</Form.Label>
            <Form.Control
              type="number"
              min="2"
              max="30"
              value={formData.interval}
              onChange={(e) => handleInputChange('interval', parseInt(e.target.value))}
              disabled={!formData.autoPlay}
            />
            <Form.Text className="text-muted">
              How long each banner shows before transitioning
            </Form.Text>
          </Form.Group>

          <hr />

          <h6 className="mb-3">
            <i className="bi-images me-2"></i>Banners in Carousel ({formData.bannerIds.length})
          </h6>

          {/* Add Banner to Carousel */}
          <div className="d-flex mb-3">
            <Form.Select
              value={selectedBanner}
              onChange={(e) => setSelectedBanner(e.target.value)}
              className="me-2"
            >
              <option value="">Select a banner to add...</option>
              {availableBanners.map(banner => (
                <option key={banner.id} value={banner.id}>
                  {banner.title} ({banner.position})
                </option>
              ))}
            </Form.Select>
            <Button 
              variant="outline-primary" 
              onClick={handleAddBanner}
              disabled={!selectedBanner}
            >
              <i className="bi-plus-circle me-1"></i>Add
            </Button>
          </div>

          {/* Selected Banners List */}
          {formData.bannerIds.length > 0 ? (
            <ListGroup>
              {formData.bannerIds.map((bannerId, index) => {
                const banner = getBannerById(bannerId);
                if (!banner) return null;
                
                return (
                  <ListGroup.Item key={bannerId} className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center flex-grow-1">
                      <div className="me-3">
                        <Badge bg="primary">{index + 1}</Badge>
                      </div>
                      <div 
                        className="bg-light rounded d-flex align-items-center justify-content-center me-3"
                        style={{ width: '40px', height: '30px' }}
                      >
                        <i className="bi-image text-muted"></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-medium">{banner.title}</div>
                        <small className="text-muted">{banner.description}</small>
                      </div>
                    </div>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => moveBanner(index, 'up')}
                        disabled={index === 0}
                        title="Move up"
                      >
                        <i className="bi-arrow-up"></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => moveBanner(index, 'down')}
                        disabled={index === formData.bannerIds.length - 1}
                        title="Move down"
                      >
                        <i className="bi-arrow-down"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveBanner(bannerId)}
                        title="Remove from carousel"
                      >
                        <i className="bi-x-lg"></i>
                      </Button>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          ) : (
            <Card body className="text-center text-muted">
              <i className="bi-images display-4 mb-2"></i>
              <p className="mb-0">No banners added yet. Select banners from the dropdown above.</p>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {mode === 'add' ? 'Create Carousel' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BannerGroupModal;

